import type { SupabaseClient } from '@supabase/supabase-js';
import { renderMappingTab } from '../tabs/kaufland-list-mapping.js';
import { renderPreviewTab } from '../tabs/kaufland-list-preview.js';
import { renderImagesTab } from '../tabs/kaufland-list-images.js';
import { renderVariantsTab } from '../tabs/kaufland-list-variants.js';
import { renderVariantImageGridForListing } from '../tabs/kaufland-variant-images-grid.js';
import { showToast } from '@vario-connect/frontend-design';
import { normalizeListingMode, publishButtonState, statusSatz, type PublishButtonState } from '../../lib/publish-button.js';
import { ladeVarioBasisUrl } from '../../lib/vario-base-url.js';
import { naechsterTakt } from '../../lib/status-watch.js';
import { produktstandHinweis, darfAutomatischAbfragen, abfrageMeldung } from '../../lib/produktstand.js';
import { bestimmeModusWahl } from '../../lib/listing-mode-choice.js';
import {
  buildOfferFormPrefill,
  createOfferSubmitLock,
  isOfferFormEditable,
  localeForStorefront,
  missingOfferFieldsMessage,
  offerSubmitGate,
  readOfferFormRaw,
  renderOfferFormFields,
  resolveOfferOverridesFromUnit,
  saveAndEnqueueOffer,
  saveAndEnqueueOverrides,
  type OfferDefaults,
  type ResolvedVarioValues,
  type ShippingGroupRow,
  type VatIndicatorRow,
} from './offer-conditions-form.js';
import { callFn } from './mapping-field-row.js';
import {
  beschreibeBestandsvorschlag,
  beschreibePreisvorschlag,
  renderPriceTakeover,
  renderStockTakeover,
  wireTakeover,
  type PreisvorschlagAnzeige,
  type PriceSuggestionResp,
} from './field-takeover.js';

/** Zweistufige Bestätigung (Projektregel: keine window.confirm/alert).
 *  Erster Klick „schärft" den Button (Label-Wechsel + danger-Optik), nach 4s
 *  ohne zweiten Klick fällt er zurück. Zweiter Klick führt onConfirm aus. */
export function wireTwoStepConfirm(
  btn: HTMLButtonElement,
  armedHtml: string,
  onConfirm: () => void,
): void {
  const idleHtml = btn.innerHTML;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const disarm = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    btn.dataset['armed'] = '';
    btn.innerHTML = idleHtml;
    btn.classList.remove('klst-btn-armed');
  };
  btn.addEventListener('click', () => {
    if (btn.dataset['armed'] === '1') { disarm(); onConfirm(); return; }
    btn.dataset['armed'] = '1';
    btn.innerHTML = armedHtml;
    btn.classList.add('klst-btn-armed');
    timer = setTimeout(disarm, 4000);
  });
}

// ============================================================================
// Geteiltes Listing-Detail-Pane — wird vom Lister (Master-Detail, rechte
// Spalte) UND vom Vario-Slot listing-status (Configure-Modus im Artikel)
// gerendert. Extrahiert aus kaufland-list.ts (2026-06-03), Verhalten 1:1.
//
// Host-Vertrag:
//   * ctx liefert Supabase-Client + Session + supabaseUrl (beide Hosts haben das).
//   * paneHost.kind steuert NICHTS funktional — beide Orte verhalten sich gleich;
//     das Feld existiert für CSS-/Telemetrie-Zwecke und künftige Abweichungen.
//   * onListingChanged(): Status/Inhalt des Listings hat sich geändert (Publish
//     enqueued, archiviert, Modus gewechselt) — Lister refresht die Master-Liste,
//     Slot refresht seinen Inbox-Status.
//   * onDeleted(): Listing wurde gelöscht — Lister leert das Detail + reloadet,
//     Slot fällt zurück in den Kategorie-Choice-Modus.
// ============================================================================

export interface DetailPaneCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

export interface DetailPaneHost {
  kind: 'lister' | 'slot';
  onListingChanged(): void;
  onDeleted(): void;
}

// ============================================================================
// Typen + Konstanten (1:1 aus kaufland-list.ts übernommen)
// ============================================================================

// 'optional' ist am 2026-08-06 entfallen: der Reiter "Optionale Felder" zeigte nach dem Umbau auf
// das Mapping-Modell dieselben Zeilen wie die Gruppe "Optionale Felder" im Mapping-Editor. Der
// Mindestpreis, sein einziger eigener Inhalt, steht jetzt dort als Direktwert-Zeile.
type DetailTab = 'overview' | 'variants' | 'images' | 'mapping' | 'preview';

export const STOREFRONT_LABELS: Record<string, string> = {
  de: 'Deutschland',
  at: 'Österreich',
  cz: 'Tschechien',
  sk: 'Slowakei',
  pl: 'Polen',
  fr: 'Frankreich',
  it: 'Italien',
};

export interface ListingDetail {
  id: string;
  vario_article_id: string;
  vario_listing_id: string;
  vario_article_number: string | null;
  vario_article_name: string | null;
  storefront: string;
  vario_category_id: string | null;
  vario_category_name: string | null;
  kaufland_category_id: number | null;
  vario_listing_snapshot: Record<string, unknown> | null;
  is_variant_parent: boolean;
  mapping_override: Record<string, unknown> | null;
  status: ListingStatus;
  last_error: string | null;
  kaufland_product_id: string | null;
  kaufland_unit_id: string | null;
  kaufland_offer_id: string | null;
  first_seen_at: string;
  last_scan_at: string;
  updated_at: string;
  /** Aufgehydratet via separates select auf kaufland.listings (kaufland_get_listing_detail
   *  liefert die Spalten nicht). Fallback 'new'. 'unknown' nur bei fehlgeschlagenem
   *  Lookup (siehe Hydration in renderListingDetailPane) - steuert den Listen-
   *  Button-Tooltip aus publishButtonState(), NICHT den attach-Panel-Zweig
   *  (detail.mode === 'attach' bleibt false, faellt also auf den normalen Detail-
   *  Render mit Tabs zurueck). 'undecided' hat seit Task 6 einen eigenen Render-
   *  Zweig (renderModeChoicePanel, VOR dem attach-Zweig geprueft): der Haendler
   *  entscheidet dort per kaufland_set_listing_mode zwischen Aufschalten und
   *  Neu-Listen, siehe bestimmeModusWahl() in lib/listing-mode-choice.ts. */
  mode?: 'new' | 'attach' | 'undecided' | 'unknown';
  attach_id_product?: number | null;
  /** Kauflands letzte Rueckmeldung waehrend der Pruefung, aus derselben Hydration wie
   *  `mode`. Nur bei status='validating' von Belang. */
  kaufland_status_reason?: string | null;
  /** Kauflands Sicht auf das PRODUKT (`SUCCESS` | `FAIL` | `PENDING`), getrennt vom Angebot.
   *  Der Angebots-Spiegel weiss nichts ueber Produktmaengel -- deshalb kann „Gelistet, ohne
   *  Bestand" danebenstehen, waehrend das Produkt noch Fehler hat. Siehe `lib/produktstand.ts`. */
  kaufland_status?: string | null;
  /** Wann dieser Produktstand zuletzt bei Kaufland geholt wurde (ISO). */
  kaufland_status_polled_at?: string | null;
  /** Zustand der Kaufland-Unit, aus derselben Hydration. Nur bei status='published' von
   *  Belang -- siehe listingPill. */
  unit?: UnitState | null;
}

export type ListingStatus =
  | 'pending'
  | 'mapping'
  | 'mapped'
  | 'queued'
  | 'validating'
  | 'published'
  | 'error'
  | 'archived';

/** Wrapper um publishButtonState(): `mode` ist auf ListingDetail optional
 *  (exactOptionalPropertyTypes verbietet ein explizites `mode: undefined`
 *  im Aufruf), deshalb hier die Property komplett auslassen statt undefined
 *  mitzugeben, wenn sie fehlt. */
function publishStateFor(status: ListingStatus, mode: 'new' | 'attach' | 'undecided' | 'unknown' | undefined): PublishButtonState {
  return publishButtonState(mode !== undefined ? { status, mode } : { status });
}

/** Innenleben des Push-Buttons (Icon + Beschriftung). Die Beschriftung wechselt
 *  mit dem Modus, deshalb wird sie bei jeder Wiederherstellung aus dem Zustand
 *  NEU GEBAUT statt einmal aus dem DOM eingefroren. Ein aus btn.innerHTML
 *  gelesener Schnappschuss koennte den Busy-Text "Prüfe…" enthalten und ihn
 *  dauerhaft einbrennen. */
function publishBtnInner(label: string): string {
  return `<span class="v-icon fa-regular fa-arrow-up"></span> ${escapeHtml(label)}`;
}

/**
 * Banner waehrend Kauflands Pruefung. Neutral, nicht rot: Warten ist kein Fehler.
 *
 * Ohne dieses Banner steht im Lister ausschliesslich die Pille "In Pruefung", und der
 * Haendler sieht tagelang nicht, WORAUF gewartet wird. Am 2026-08-07 waren es fehlende
 * Attribute -- die Kaufland ausdruecklich NICHT als Fehler fuehrt, die die Freigabe aber
 * dauerhaft blockieren. Der Grund steht roh da (`MISSING_ATTRIBUTES`), weil die Liste
 * moeglicher Werte nicht gemessen ist und eine Uebersetzung geraten waere.
 */
/**
 * Beobachtet ein Listing nach einem Klick auf "Listen" und meldet jede Aenderung.
 *
 * Warum es das braucht: Auf dem Listen-Weg lud das Detail nach dem Klick GENAU EINMAL neu.
 * Danach stand dort "Wird gelistet", und die naechsten Uebergaenge -- Einreichung raus,
 * Kauflands erste Rueckmeldung -- kamen erst, wenn der Haendler von sich aus neu lud. Das
 * Aufschalten-Formular hatte so eine Beobachtung laengst, der Listen-Knopf nicht.
 *
 * Der Timer liegt bewusst MODUL-weit und nicht im Pane: `onChange` rendert das Pane neu, und
 * ein im Pane gehaltener Timer haette sich damit nach der ersten Aenderung selbst abgeraeumt
 * -- man saehe genau einen Uebergang statt aller.
 *
 * Ende: bei einem Endzustand, sobald Kauflands Wartegrund da ist (danach passiert eine halbe
 * Stunde nichts mehr), oder nach der Obergrenze. Ein zweiter Klick loest den alten Beobachter
 * ab, es laeuft nie mehr als einer je Listing.
 */
const statusWatchers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * Was Kaufland ueber die angelegte Unit sagt. Nur die zwei Felder, die fuer die Anzeige
 * zaehlen; beide werden von den `item_unit_*`-Webhooks gepflegt.
 */
export interface UnitState {
  status: string | null;
  stock: number | null;
}

/**
 * Die Pille fuer ein Listing -- und der Grund, warum es diese Funktion gibt.
 *
 * `kaufland.listings.status = 'published'` heisst woertlich nur: "wir haben die Unit
 * angelegt". Ob jemand das Angebot KAUFEN kann, steht damit nicht fest. Am 2026-08-07 hat
 * genau das in die Irre gefuehrt: Das Listing zeigte "Gelistet", bei Kaufland stand die Unit
 * auf `AVAILABLE` -- mit `amount: 0`. Nicht kaufbar, und die Anzeige behauptete das Gegenteil.
 *
 * Ein Angebot ohne Bestand anzulegen ist ausdruecklich in Ordnung: Kaufland nimmt es, und der
 * Bestands-Sync schiebt die Menge nach, sobald Vario welche meldet. Falsch war nie der Push,
 * sondern das Wort.
 *
 * Deshalb faechert `published` hier in drei Faelle auf, sobald der Unit-Zustand bekannt ist:
 *   * aktiv mit Bestand      -> "Gelistet"
 *   * aktiv ohne Bestand     -> "Gelistet, ohne Bestand" (gedaempft, nicht gruen)
 *   * nicht aktiv / geloescht-> "Pausiert"
 *
 * Ohne Unit-Daten (`null`) bleibt es bei der alten Pille. Das ist kein Versaeumnis, sondern
 * die ehrliche Antwort: Wir wissen es dann schlicht nicht, und eine erfundene Praezisierung
 * waere derselbe Fehler noch einmal.
 */
export function listingPill(
  status: ListingStatus,
  unit?: UnitState | null,
): { cls: string; label: string } {
  const base = STATUS_PILL[status] ?? STATUS_PILL.pending;
  if (status !== 'published' || !unit) return base;
  const aktiv = typeof unit.status === 'string' && unit.status.toLowerCase() === 'active';
  if (!aktiv) return { cls: 'v-pill--muted', label: 'Pausiert' };
  if ((unit.stock ?? 0) <= 0) return { cls: 'v-pill--muted', label: 'Gelistet, ohne Bestand' };
  return base;
}

/**
 * Der Satz ueber dem Angebots-Hinweis. Bis zum 2026-08-08 stand dort unbedingt "Angebot ist
 * auf Kaufland aktiv" -- auch dann, wenn die Unit gar keinen Bestand hatte und niemand kaufen
 * konnte. Dieselbe Unwahrheit wie bei der Pille, nur ausformuliert.
 */
function unitSatz(d: { status: ListingStatus; unit?: UnitState | null }): string {
  const pill = listingPill(d.status, d.unit);
  if (pill.label === 'Gelistet, ohne Bestand') {
    return 'Angebot liegt bei Kaufland, ist aber ohne Bestand nicht kaufbar.';
  }
  if (pill.label === 'Pausiert') return 'Angebot ist bei Kaufland derzeit pausiert.';
  return 'Angebot ist auf Kaufland aktiv.';
}

export function watchListingStatus(
  ctx: DetailPaneCtx,
  listingId: string,
  seen: { status: string; reason: string | null },
  onChange: () => void,
): void {
  stopWatchingListing(listingId);
  const beginn = Date.now();
  const tick = async (): Promise<void> => {
    const { data, error } = await ctx.supabase
      .schema('kaufland')
      .from('listings')
      .select('status, kaufland_status_reason')
      .eq('id', listingId)
      .maybeSingle();
    if (error || !data) { stopWatchingListing(listingId); return; }
    const row = data as { status: string; kaufland_status_reason: string | null };
    const reason = row.kaufland_status_reason ?? null;

    if (row.status !== seen.status || reason !== seen.reason) {
      seen.status = row.status;
      seen.reason = reason;
      // Der Host kann inzwischen weg sein (Tab-Wechsel, anderes Listing gewaehlt).
      try { onChange(); } catch { stopWatchingListing(listingId); return; }
    }

    // Bis zum 2026-08-20 stand hier zusaetzlich
    //   `const nichtsMehrZuSehen = row.status === 'validating' && reason !== null;`
    // und brach die Beobachtung ab, sobald Kaufland zum ERSTEN Mal irgendeinen Wartegrund
    // lieferte. Die Anzeige fror damit auf diesem Zwischenstand ein -- der Betreiber sah
    // "fehlende Attribute: Beschreibung" noch lange, nachdem das Listing freigegeben war.
    // Ein Wartegrund ist genau der Zustand, in dem weiterzuschauen ist. Regeln jetzt in
    // `naechsterTakt` (lib/status-watch.ts), dort auch getestet.
    const takt = naechsterTakt({ status: row.status, reason, verstrichenMs: Date.now() - beginn });
    if (!takt.weiter) { stopWatchingListing(listingId); return; }
    statusWatchers.set(listingId, setTimeout(() => { void tick(); }, takt.delayMs));
  };
  const ersterTakt = naechsterTakt({ status: seen.status, reason: seen.reason, verstrichenMs: 0 });
  statusWatchers.set(listingId, setTimeout(() => { void tick(); }, ersterTakt.delayMs));
}

export function stopWatchingListing(listingId: string): void {
  const t = statusWatchers.get(listingId);
  if (t !== undefined) { clearTimeout(t); statusWatchers.delete(listingId); }
}

function waitBanner(d: { status: ListingStatus; kaufland_status_reason?: string | null }): string {
  // Das Banner erscheint jetzt bei JEDEM `validating`, auch ohne Wartegrund -- sonst gaebe es
  // in genau der Phase, in der man nachsehen will, keinen Aktualisieren-Knopf.
  if (d.status !== 'validating') return '';
  const grund = d.kaufland_status_reason
    // "Zwischenstand", nicht "Rückmeldung": Kaufland nennt hier, was im Katalogeintrag in
    // diesem Moment noch fehlt, nicht ein Urteil ueber unsere Einreichung. Am 2026-08-20
    // fuehrte die alte Wortwahl zu der Annahme, das Mapping sei kaputt -- die Beschreibung
    // war nachweislich eingereicht und wurde 31 Minuten spaeter freigegeben.
    ? `<div><strong>Kaufland prüft.</strong> Zwischenstand: ${escapeHtml(d.kaufland_status_reason)}</div>`
    : `<div><strong>Kaufland prüft die Produktdaten.</strong> Die Freigabe dauert erfahrungsgemäß 1 bis 30 Minuten.</div>`;
  return `
    <div class="v-banner v-banner-info klst-detail-wait">
      <span class="v-icon fa-light fa-clock"></span>
      ${grund}
      <button type="button" class="v-button v-button--sm klst-status-refresh">
        <span class="v-icon fa-regular fa-arrows-rotate"></span> Jetzt aktualisieren
      </button>
    </div>`;
}

/** Banner mit Kauflands Sicht auf das PRODUKT — neben der Pille, die das ANGEBOT zeigt.
 *
 *  Anlass (2026-08-22): „Kaufland meldet manchmal gelistet, ohne Bestand … in Wahrheit gibt es
 *  aber noch Fehler im Produkt." Die Pille kommt aus `kaufland.units` und weiss nichts ueber
 *  Produktmaengel; diese Zeile schliesst die Luecke. Regeln in `lib/produktstand.ts`. */
function produktstandBanner(d: ListingDetail): string {
  const h = produktstandHinweis({
    status: d.status,
    kauflandStatus: d.kaufland_status ?? null,
    reason: d.kaufland_status_reason ?? null,
    polledAtMs: d.kaufland_status_polled_at ? Date.parse(d.kaufland_status_polled_at) : null,
    jetztMs: Date.now(),
  });
  if (!h) return '';
  const klasse = h.ton === 'ok' ? 'v-banner-success'
    : h.ton === 'fehler' ? 'v-banner-danger'
    : h.ton === 'warnung' ? 'v-banner-warning' : 'v-banner-info';
  const icon = h.ton === 'ok' ? 'fa-circle-check' : h.ton === 'neutral' ? 'fa-circle-info' : 'fa-triangle-exclamation';
  return `
    <div class="v-banner ${klasse} klst-detail-wait klst-produktstand">
      <span class="v-icon fa-light ${icon}"></span>
      <div><strong>Produkt bei Kaufland:</strong> ${escapeHtml(h.text)}</div>
      <button type="button" class="v-button v-button--sm klst-produktstand-refresh">
        <span class="v-icon fa-regular fa-arrows-rotate"></span> Jetzt prüfen
      </button>
    </div>`;
}

/**
 * Verdrahtet den Aktualisieren-Knopf im Wartebanner.
 *
 * Ruft `kaufland_enqueue_status_refresh` (Migration 0056) -- NICHT
 * `kaufland_enqueue_listing_publish`: jene setzt `product_data_submitted_at` zurueck und
 * schickte damit ein zweites, nicht zuruecknehmbares `PUT /v2/product-data/` auf dieselbe EAN.
 *
 * Der Worker nimmt den vorgezogenen Job binnen seines Backstop-Takts auf (Default 30 s), der
 * Text sagt das deshalb auch so. Die Beobachtung laeuft ohnehin weiter und zeigt das Ergebnis.
 */
/**
 * Verdrahtet die Produktstand-Abfrage: Knopf UND automatisch beim Oeffnen.
 *
 * Ruft `kaufland_enqueue_product_status_check` (Migration 0057) -- NICHT
 * `kaufland_enqueue_status_refresh`: jene zieht `kaufland.publish_listing` vor, und der liefe
 * bei einem freigegebenen Listing in die ANGEBOTS-ANLAGE statt in eine Abfrage. Die RPC hier
 * reiht den rein lesenden `kaufland.poll_listing_status` ein.
 *
 * Die automatische Abfrage beim Oeffnen ist der ausdrueckliche Wunsch des Betreibers
 * (2026-08-22). Sie greift nur, wenn der Stand aelter als `FRISCHE_MS` ist -- dieselbe
 * Schwelle steckt in der RPC, damit beide Seiten dasselbe meinen und ein Aufklappen nicht
 * jedes Mal einen Kaufland-Aufruf kostet.
 */
function wireProduktstand(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail, onChange: () => void): void {
  const abfragen = async (): Promise<string | null> => {
    const { error } = await ctx.supabase.rpc('kaufland_enqueue_product_status_check', { _listing_id: detail.id });
    if (error) {
      console.warn('[listing-detail-pane] Produktstand-Abfrage fehlgeschlagen:', error.message);
      return error.message;
    }
    // Der Worker braucht einen Moment; die bestehende Beobachtung zeigt das Ergebnis, sobald
    // die Spalten stehen.
    watchListingStatus(ctx, detail.id, { status: detail.status, reason: detail.kaufland_status_reason ?? null }, onChange);
    return null;
  };

  const btn = mount.querySelector<HTMLButtonElement>('.klst-produktstand-refresh');
  btn?.addEventListener('click', () => {
    void (async () => {
      const vorher = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Wird geprüft …';
      const fehler = await abfragen();
      const m = abfrageMeldung(fehler);
      showToast(m.text, m.art);
      window.setTimeout(() => {
        if (btn.isConnected) { btn.disabled = false; btn.innerHTML = vorher; }
      }, 5000);
    })();
  });

  if (darfAutomatischAbfragen({
    status: detail.status,
    polledAtMs: detail.kaufland_status_polled_at ? Date.parse(detail.kaufland_status_polled_at) : null,
    jetztMs: Date.now(),
  })) {
    void abfragen();
  }
}

function wireStatusRefresh(mount: HTMLElement, ctx: DetailPaneCtx, listingId: string): void {
  const btn = mount.querySelector<HTMLButtonElement>('.klst-status-refresh');
  if (!btn) return;
  btn.addEventListener('click', () => {
    void (async () => {
      const vorher = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Wird abgerufen …';
      const { error } = await ctx.supabase.rpc('kaufland_enqueue_status_refresh', { _listing_id: listingId });
      if (error) {
        showToast(`Abruf fehlgeschlagen: ${error.message}`, 'error');
      } else {
        showToast('Kaufland wird in den nächsten Sekunden abgefragt.', 'info');
      }
      // Zweistufig entsperren: erst nach kurzer Sperre wieder klickbar, damit ein
      // ungeduldiger Doppelklick nicht zwei Abrufe ausloest.
      window.setTimeout(() => {
        if (btn.isConnected) { btn.disabled = false; btn.innerHTML = vorher; }
      }, 5000);
    })();
  });
}

export const STATUS_PILL: Record<ListingStatus, { cls: string; label: string }> = {
  pending:    { cls: 'v-pill--info',  label: 'Neu' },
  mapping:    { cls: 'v-pill--warn',  label: 'In Bearbeitung' },
  mapped:     { cls: 'v-pill--ok',    label: 'Bereit' },
  queued:     { cls: 'v-pill--info',  label: 'Wird gelistet' },
  validating: { cls: 'v-pill--info',  label: 'In Prüfung' },
  published:  { cls: 'v-pill--ok',    label: 'Gelistet' },
  error:      { cls: 'v-pill--error', label: 'Fehler' },
  archived:   { cls: 'v-pill--muted', label: 'Archiviert' },
};

// ============================================================================
// Vario-Base-URL — Modul-Level-Memo
// ============================================================================

// Vario-Base-URL einmal pro Page-Load ziehen. Modul-Level-Memo: Session/Tenant sind pro
// iframe-Load fix. Auswahl oeffentlich-vor-abgeleitet steckt in `waehleVarioBasisUrl`.
let varioBaseUrlPromise: Promise<string | null> | null = null;
function getVarioBaseUrl(supabase: SupabaseClient): Promise<string | null> {
  return (varioBaseUrlPromise ??= ladeVarioBasisUrl(supabase));
}

// ============================================================================
// Entry-Funktion
// ============================================================================

/**
 * Der EINE Absende-Weg fuer die Verkaufsbedingungen: Werte aus dem Formular lesen,
 * pruefen, speichern, Push einreihen. Alle drei Knoepfe im Lister laufen hier
 * durch, damit es nicht wieder zwei Knoepfe mit unterschiedlicher Bedeutung gibt:
 *   * „Auf Kaufland aufschalten" im Aufschalt-Panel (mode='attach'),
 *   * „Jetzt auf Kaufland listen" im Ueberblick-Abschnitt (mode='new'),
 *   * „Listen" in der Kopfleiste (mode='new').
 *
 * Ein Klick genuegt, es gibt bewusst keine zweite Bestaetigung.
 *
 * Rueckgabe true heisst: gespeichert UND eingereiht. Bei false hat setStatus()
 * bereits den Grund geschrieben.
 */
async function submitOfferForm(
  ctx: DetailPaneCtx,
  listingId: string,
  form: HTMLFormElement,
  setStatus: (msg: string, kind: 'info' | 'error') => void,
): Promise<boolean> {
  const res = await saveAndEnqueueOffer(readOfferFormRaw(form), {
    // Direkt-UPDATE auf kaufland.listings ist der Rolle authenticated nicht erlaubt
    // (nur SELECT-Grant). Die RPC kapselt das Snapshot-Merge inkl. Modus- und
    // Status-Vorbedingung, siehe Migration 0029.
    saveOverrides: async (overrides) => {
      const { error } = await ctx.supabase.rpc('kaufland_save_attach_overrides', {
        _listing_id: listingId,
        _overrides: overrides,
      });
      return { error };
    },
    enqueuePublish: async () => {
      const { error } = await ctx.supabase.rpc('kaufland_enqueue_listing_publish', { _listing_id: listingId });
      return { error };
    },
    onStage: (stage) => {
      setStatus(stage === 'save' ? 'Speichere Verkaufsbedingungen…' : 'Stelle Listing in die Push-Queue…', 'info');
    },
  });

  if (!res.ok) {
    const prefix = res.stage === 'save' ? 'Speichern fehlgeschlagen: '
      : res.stage === 'enqueue' ? 'Queue-Fehler: '
      : '';
    setStatus(`${prefix}${res.message}`, 'error');
    return false;
  }
  return true;
}

export async function renderListingDetailPane(
  mount: HTMLElement,
  ctx: DetailPaneCtx,
  listingId: string,
  paneHost: DetailPaneHost,
): Promise<void> {
  mount.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Details…</div>`;
  const { data, error } = await ctx.supabase.rpc('kaufland_get_listing_detail', { _id: listingId });
  if (error) {
    mount.innerHTML = `<div class="klst-loading klst-loading--error">Fehler: ${escapeHtml(error.message)}</div>`;
    return;
  }
  const detail = (Array.isArray(data) ? data[0] : data) as ListingDetail | null | undefined;
  if (!detail) {
    mount.innerHTML = `<div class="klst-loading">Listing nicht gefunden.</div>`;
    return;
  }

  // Hydrate mode + attach_id_product — kaufland_get_listing_detail liefert die
  // Spalten nicht. Sequentieller select, Fehler ist fail-soft (fallback 'new').
  const { data: modeRow, error: modeErr } = await ctx.supabase
    .schema('kaufland')
    .from('listings')
    .select('mode, attach_id_product, kaufland_status_reason, kaufland_status, kaufland_status_polled_at')
    .eq('id', detail.id)
    .maybeSingle();
  if (modeErr) {
    console.warn('[listing-detail-pane] detail mode hydration failed:', modeErr.message);
    // NICHT als 'new' interpretieren: mode='unknown' gibt dem Listen-Button einen
    // eigenen Tooltip fuer den transienten Fehlerfall (siehe publishButtonState in
    // lib/publish-button.ts), statt faelschlich "Neu listen ist nicht freigeschaltet"
    // zu zeigen.
    detail.mode = 'unknown';
  } else if (modeRow) {
    const row = modeRow as {
      mode?: string | null;
      attach_id_product?: number | null;
      kaufland_status_reason?: string | null;
      kaufland_status?: string | null;
      kaufland_status_polled_at?: string | null;
    };
    detail.mode = normalizeListingMode(row.mode);
    detail.attach_id_product = row.attach_id_product ?? null;
    detail.kaufland_status_reason = row.kaufland_status_reason ?? null;
    detail.kaufland_status = row.kaufland_status ?? null;
    detail.kaufland_status_polled_at = row.kaufland_status_polled_at ?? null;
  }

  // Zustand der Kaufland-Unit. Nur wenn es eine gibt -- also erst nach dem Aufschalten.
  // Fail-soft: ohne diese Daten bleibt die Pille bei "Gelistet", siehe listingPill.
  if (detail.kaufland_unit_id) {
    const { data: unitRow, error: unitErr } = await ctx.supabase
      .schema('kaufland')
      .from('units')
      .select('status, stock')
      .eq('id_unit', detail.kaufland_unit_id)
      .maybeSingle();
    if (unitErr) {
      console.warn('[listing-detail-pane] unit lookup failed:', unitErr.message);
    } else if (unitRow) {
      const u = unitRow as { status: string | null; stock: number | null };
      detail.unit = { status: u.status ?? null, stock: u.stock ?? null };
    }
  }

  // Render-Verzweigung: mode='undecided' zeigt die Modus-Entscheidung statt
  // eines der beiden Panels -- MUSS vor dem attach-Zweig geprueft werden, sonst
  // ist der Zweig nie erreichbar (detail.mode kann hier nur einen Wert haben).
  if (detail.mode === 'undecided') {
    await renderModeChoicePanel(mount, ctx, detail, () => paneHost.onListingChanged());
    return;
  }

  // mode='attach' rendert den reduzierten Aufschalten-Single-Panel statt
  // Mapping/Bilder/Preview-Tabs.
  if (detail.mode === 'attach') {
    await renderAttachPanel(mount, ctx, detail, () => paneHost.onListingChanged());
    return;
  }

  mount.innerHTML = renderDetail(detail);
  wireDetailTabs(mount, ctx, detail);
  wireStatusRefresh(mount, ctx, detail.id);
  wireProduktstand(mount, ctx, detail, () => paneHost.onListingChanged());

  const archiveBtn = mount.querySelector<HTMLButtonElement>('#klst-detail-archive');
  archiveBtn?.addEventListener('click', () => void archiveListing(detail.id, detail.status !== 'archived'));
  const deleteBtn = mount.querySelector<HTMLButtonElement>('#klst-detail-delete');
  if (deleteBtn) {
    wireTwoStepConfirm(
      deleteBtn,
      `<span class="v-icon fa-regular fa-trash"></span> Wirklich löschen?`,
      () => void deleteListing(detail),
    );
  }

  // Listen-Button in der Tab-Leiste (links vom Vario-Link) — Portierung von
  // doPublishSelected() aus kaufland-list.ts (vormals im apphead, Task
  // "Lister-Listen-Button"). Verhalten 1:1 erhalten, nur onListingChanged()
  // statt reloadList(), weil dieses Modul den Master nicht kennt.
  const publishBtn = mount.querySelector<HTMLButtonElement>('#klst-detail-publish');
  // Beschriftung NIE aus btn.innerHTML zurueckholen, sondern aus dem Zustand neu
  // bauen. Ein DOM-Schnappschuss koennte den Busy-Text "Prüfe…" enthalten und ihn
  // dauerhaft einbrennen; ausserdem wechselt die Beschriftung mit dem Modus.
  const publishIdleHtml = publishBtnInner(publishStateFor(detail.status, detail.mode).label);

  /** Sperrt den Listen-Knopf fuer die Dauer eines Absendevorgangs.
   *
   *  Seit dem 2026-08-07 gibt es nur noch diesen einen Knopf, der zweite im
   *  Ueberblick-Abschnitt ist mit dem Abschnitt entfallen. Die Sperre bleibt trotzdem,
   *  und zwar nicht aus Kosmetik: der Ablauf ist mit der Mapping-Aufloesung LAENGER
   *  geworden (Profil, Vorschau-Function mit Vario-Kaskaden, Offer-Defaults, dann erst
   *  Speichern und Einreihen). In diesem Fenster ist ein zweiter Klick leicht, und
   *  `kaufland_enqueue_listing_publish` prueft seine Idempotenz mit einem ungesperrten
   *  SELECT-dann-INSERT. Zwei Jobs waeren zwei Produktdaten-Einreichungen, und dafuer
   *  gibt es kein DELETE. Begruendung ausfuehrlich an createOfferSubmitLock().
   *
   *  Beim Freigeben wird NICHT pauschal aktiviert, sondern der gueltige Zustand aus
   *  publishButtonState wiederhergestellt. Sonst wuerde ein gesperrter Knopf durch das
   *  Freigeben faelschlich klickbar. */
  const setOfferButtonsBusy = (busy: boolean): void => {
    const kopf = mount.querySelector<HTMLButtonElement>('#klst-detail-publish');
    if (kopf?.isConnected) {
      kopf.disabled = busy || !publishStateFor(detail.status, detail.mode).enabled;
    }
  };

  // Scope ist EIN renderListingDetailPane-Aufruf, nicht das Modul: jeder Detail-Wechsel
  // bekommt seine eigene Sperre.
  const offerLock = createOfferSubmitLock(setOfferButtonsBusy);
  publishBtn?.addEventListener('click', () => void doPublishListing());

  // Artikel-oeffnen-Icon-Link in der Tab-Bar (rechts) — async populiert wenn die
  // tenant-base-URL geladen ist. Bis dahin ist der Link versteckt.
  const articleLink = mount.querySelector<HTMLAnchorElement>('#klst-detail-vario-link');
  if (articleLink) {
    void getVarioBaseUrl(ctx.supabase).then((baseUrl) => {
      if (!baseUrl) return;
      articleLink.href = `${baseUrl}/products/articles/${encodeURIComponent(detail.vario_article_id)}/detail`;
      articleLink.style.display = '';
    });
  }

  async function archiveListing(id: string, archive: boolean) {
    const { error: archErr } = await ctx.supabase.rpc('kaufland_archive_listing', { _id: id, _archive: archive });
    if (archErr) {
      showToast(`Archivieren fehlgeschlagen: ${archErr.message}`, 'error');
      return;
    }
    paneHost.onListingChanged();
  }

  async function deleteListing(d: ListingDetail) {
    const { error: delErr } = await ctx.supabase.rpc('kaufland_delete_listing', { _id: d.id });
    if (delErr) {
      showToast(`Löschen fehlgeschlagen: ${delErr.message}`, 'error');
      return;
    }
    showToast('Listing gelöscht.', 'success');
    paneHost.onDeleted();
  }

  /** Schaltet das aktuell angezeigte Listing auf, und zwar ueber DENSELBEN Weg wie
   *  der Knopf im Abschnitt: Formularwerte uebernehmen, speichern, einreihen.
   *
   *  Frueher las dieser Knopf nur die gespeicherten Overrides und sperrte, wenn dort
   *  nichts stand. Bei mode='new' stand dort nie etwas, weil es kein Formular gab -
   *  er meldete "Verkaufspreis fehlt" und verwies auf ein Formular, das es nicht gab.
   *  Zwei Knoepfe mit unterschiedlicher Bedeutung waren die Ursache der Verwirrung,
   *  deshalb tun jetzt beide dasselbe.
   *
   *  Bewusst als const-Arrow-Function (nicht `function`-Deklaration) definiert:
   *  nur so behaelt TS die weiter oben durchgefuehrte Null-Pruefung von `detail`
   *  innerhalb dieses Closures - eine hoisted function-Deklaration verwirft die
   *  Narrowing, weil sie theoretisch schon vor der Pruefung aufrufbar waere. */
  const doPublishListing = async (): Promise<void> => {
    const btn = publishBtn;
    const id = detail.id;
    if (!btn) return;

    // Synchron VOR dem ersten await. Ein spaeteres Erwerben liesse genau die
    // Luecke offen, die die Sperre schliessen soll.
    if (!offerLock.tryAcquire()) return;
    btn.textContent = 'Prüfe…';
    try {
      // Server-Gegenprobe in EINEM Read: der lokale `detail`-Stand kann bis zu einen
      // Reload alt sein. Status und mode stehen beide auf kaufland.listings, deshalb
      // genuegt derselbe select wie in der Mode-Hydration oben.
      const freshRes = await ctx.supabase
        .schema('kaufland')
        .from('listings')
        .select('status, mode')
        .eq('id', id)
        .maybeSingle();
      if (freshRes.error) { showToast(`Listing konnte nicht gelesen werden: ${freshRes.error.message}`, 'error'); return; }
      const fresh = freshRes.data as { status?: string; mode?: string } | null;
      if (!fresh) { showToast('Listing nicht gefunden.', 'error'); return; }

      // normalizeListingMode statt eines Inline-Ternaers: ein unlesbarer Modus wird
      // 'unknown' und damit gesperrt, NICHT 'new'. Begruendung am Helfer.
      const mode = normalizeListingMode(fresh.mode);
      const state = publishButtonState({ status: fresh.status as ListingStatus, mode });
      if (!state.enabled) { showToast(`Aufschalten nicht möglich: ${state.title}`, 'error'); return; }

      // Variantenvater zuerst, sonst laeuft er in eine Meldung ueber fehlende
      // Verkaufsbedingungen, obwohl der Worker ihn unabhaengig davon abweist
      // (kaufland-publish-listing.ts, Schritt 2a).
      if (detail.is_variant_parent) {
        showToast('Variantenvater wird bei Kaufland nicht als eigenes Produkt gelistet. Bitte die einzelnen Varianten im Reiter „Varianten" aufschalten.', 'error');
        return;
      }

      // Verkaufsbedingungen aus dem Mapping aufloesen. Das dauert einen Moment (Profil,
      // Vorschau-Function mit Vario-Kaskaden, Offer-Defaults), deshalb sagt der Knopf
      // waehrenddessen, was laeuft.
      btn.textContent = 'Lese Mapping…';
      const aufgeloest = await resolveOfferFromMapping(ctx, detail);
      if (!aufgeloest.ok) {
        showToast(`Aufschalten nicht möglich: ${aufgeloest.message}`, 'error');
        return;
      }

      btn.textContent = 'Listet…';
      const res = await saveAndEnqueueOverrides(aufgeloest.overrides, {
        // Direkt-UPDATE auf kaufland.listings ist der Rolle authenticated nicht erlaubt
        // (nur SELECT-Grant). Die RPC kapselt das Snapshot-Merge inkl. Modus- und
        // Status-Vorbedingung, siehe Migration 0029.
        saveOverrides: async (overrides) => {
          const { error } = await ctx.supabase.rpc('kaufland_save_attach_overrides', {
            _listing_id: id,
            _overrides: overrides,
          });
          return { error };
        },
        enqueuePublish: async () => {
          const { error } = await ctx.supabase.rpc('kaufland_enqueue_listing_publish', { _listing_id: id });
          return { error };
        },
      });
      if (!res.ok) {
        const prefix = res.stage === 'save' ? 'Speichern fehlgeschlagen: ' : 'Queue-Fehler: ';
        showToast(`Aufschalten nicht möglich: ${prefix}${res.message}`, 'error');
        return;
      }

      showToast('Listing zur Aufschaltung eingestellt.', 'success');
      paneHost.onListingChanged();
      // Ab hier zeigt sich der Fortschritt von selbst: 'queued' -> Einreichung raus ->
      // 'validating' -> Kauflands erste Rueckmeldung. Ohne das stand nach dem Klick
      // "Wird gelistet" und danach still nichts mehr.
      watchListingStatus(
        ctx,
        id,
        { status: detail.status, reason: detail.kaufland_status_reason ?? null },
        () => paneHost.onListingChanged(),
      );
    } finally {
      // Gibt beide Knoepfe frei, jeweils in ihren gueltigen Zustand.
      offerLock.release();
      // Mount kann waehrend des await ersetzt worden sein (Tab-Wechsel, Slot-Remount,
      // oder der onListingChanged()-Reload oben hat diesen Button bereits ausgetauscht).
      if (btn.isConnected) {
        btn.innerHTML = publishIdleHtml;
        const st = publishStateFor(detail.status, detail.mode);
        btn.disabled = !st.enabled;
        btn.title = st.title;
      }
    }
  };
}

// ============================================================================
// wireDetailTabs (aus kaufland-list.ts Z. 468–564, KauflandListCtx → DetailPaneCtx)
// ============================================================================

function wireDetailTabs(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail): void {
  const tabsBar = mount.querySelector<HTMLElement>('#klst-detail-tabs');
  const panes = mount.querySelectorAll<HTMLElement>('[data-detail-pane]');
  if (!tabsBar) return;

  const switchTo = (target: DetailTab) => {
    tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((b) => {
      b.classList.toggle('klst-tab--active', b.dataset['detailTab'] === target);
    });
    panes.forEach((p) => { p.hidden = p.dataset['detailPane'] !== target; });

    if (target === 'variants') {
      const varHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="variants"]');
      if (varHost) {
        void renderVariantsTab(varHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
        }, {
          id: detail.id,
          storefront: detail.storefront,
          kaufland_category_id: detail.kaufland_category_id,
          vario_category_id: detail.vario_category_id,
          vario_listing_id: detail.vario_listing_id,
          vario_article_id: detail.vario_article_id,
        });
      }
    }
    if (target === 'images') {
      const imgHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="images"]');
      if (imgHost) {
        const subCtx = {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
        };
        if (detail.is_variant_parent) {
          // Beim Variantenvater die MATRIX der ganzen Familie statt des Einzelartikel-Dialogs:
          // Der Vater ist bei Kaufland kein Produkt, seine eigenen Bilder werden nie gelistet.
          // Zu verteilen sind sie auf die Kinder — und genau das kann nur die Matrix.
          void renderVariantImageGridForListing(imgHost, subCtx, {
            storefront: detail.storefront,
            vario_article_id: detail.vario_article_id,
            vario_listing_id: detail.vario_listing_id,
            kaufland_category_id: detail.kaufland_category_id,
          });
        } else {
          void renderImagesTab(imgHost, subCtx, { vario_article_id: detail.vario_article_id });
        }
      }
    }
    if (target === 'mapping') {
      const mapHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="mapping"]');
      if (mapHost && !mapHost.dataset['loaded']) {
        mapHost.dataset['loaded'] = '1';
        void renderMappingTab(mapHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
          onPreviewRequested: () => switchTo('preview'),
        }, {
          storefront: detail.storefront,
          vario_category_id: detail.vario_category_id,
          kaufland_category_id: detail.kaufland_category_id,
          vario_listing_id: detail.vario_listing_id,
          vario_article_id: detail.vario_article_id,
        });
      }
    }
    if (target === 'preview') {
      const prevHost = mount.querySelector<HTMLDivElement>('[data-detail-pane="preview"]');
      if (prevHost) {
        // Preview wird bei JEDEM Klick neu gerendert — Mapping kann sich zwischen den
        // Tab-Wechseln im Mapping-Tab veraendert haben, frische Daten sind hier wichtiger
        // als Caching.
        void renderPreviewTab(prevHost, {
          supabase: ctx.supabase,
          supabaseUrl: ctx.supabaseUrl,
          session: { token: ctx.session.token },
        }, {
          storefront: detail.storefront,
          kaufland_category_id: detail.kaufland_category_id,
          vario_listing_id: detail.vario_listing_id,
          vario_article_id: detail.vario_article_id,
        });
      }
    }
  };

  tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((btn) => {
    btn.addEventListener('click', () => switchTo(btn.dataset['detailTab'] as DetailTab));
  });
}

// ============================================================================
// Render-Helper (aus kaufland-list.ts Z. 750–850, 895–912)
// ============================================================================

function renderDetail(d: ListingDetail): string {
  const snapshot = d.vario_listing_snapshot ?? {};
  const ean = stringField(snapshot, 'ean');
  const archiveLabel = d.status === 'archived' ? 'Wiederaufnehmen' : 'Archivieren';
  // Initial-Zustand des Listen-Buttons; nach jedem Klick rechnet doPublishListing()
  // (siehe unten) neu.
  const pubState = publishStateFor(d.status, d.mode);
  // `klst-detail` ist der Geltungsbereich der Entwurfs-Gestaltung
  // (styles/listing-detail-design.css). Die Klasse traegt KEINE Funktion — sie grenzt nur
  // ab, wo das Aussehen gilt, damit insbesondere das gescopete `box-sizing: border-box`
  // nicht global wird (im Projekt ausdruecklich verboten).
  // Kategorie-Leiste aus dem Entwurf: zugeordnete Kategorie plus Status.
  //
  // Sie steht HIER und nicht in der Kopfzeile der Seite (`kaufland-list.ts`), obwohl der
  // Entwurf sie dort zeigt. Grund: Sie beschreibt das ausgewaehlte Listing, nicht die Seite.
  // In der Kopfzeile muesste die Auswahl erst dorthin durchgereicht und bei jedem
  // Zeilenwechsel nachgefuehrt werden — ein Zustand mehr, der auseinanderlaufen kann. Da das
  // Detailfenster unmittelbar unter der Kopfzeile sitzt, ist der optische Unterschied keiner.
  const pill = listingPill(d.status, d.unit);

  // Name der Kategorie, sonst "Vario #<id>" — dieselbe Regel wie im Vario-Slot. Die nackte
  // ID als Ueberschrift war ein Fehler: Sie sieht aus wie ein Name und ist keiner.
  const katName = d.vario_category_name
    ? d.vario_category_name
    : (d.vario_category_id ? `Vario #${d.vario_category_id}` : 'Keine Kategorie');

  // Die IDs beziehen sich auf die KATEGORIE, nicht auf den Artikel. Meine erste Fassung
  // beschriftete hier die Artikel-ID mit "Vario-ID" und widersprach damit der Box darueber,
  // die unter derselben Beschriftung die Kategorie-ID zeigte.
  const katIds: string[] = [];
  if (d.kaufland_category_id) katIds.push(`Kaufland-ID: ${d.kaufland_category_id}`);
  if (d.vario_category_id) katIds.push(`Vario-ID: ${d.vario_category_id}`);

  // Die Statuszeile stand bis zum 2026-08-17 unter der Box „Aktuell zugeordnet" im
  // Vario-Slot. Sie ist mit der Box hierher gewandert, im selben Wortlaut.
  const satz = statusSatz({
    status: d.status,
    reason: d.kaufland_status_reason,
    lastError: d.last_error,
  });

  // Die Markenzeile stand hier bis zum 2026-08-17 und ist in den Kopf des Vario-Slots
  // gewandert (`listing-status.ts`, `.kls-head`). Sie beschreibt die App, nicht das einzelne
  // Listing — und im Lister traegt die Seite ohnehin schon eine eigene Kopfzeile. An beiden
  // Stellen haette sie doppelt gestanden.
  const catStrip = `
      <div class="klst-cat-strip">
        <span class="klst-cat-pill">
          <span class="klst-cat-ic"><span class="v-icon fa-regular fa-list-tree"></span></span>
          <span class="klst-cat-lab">Aktuell zugeordnet</span>
          <span class="klst-cat-val">${escapeHtml(katName)}</span>
          ${katIds.length > 0 ? `<span class="klst-cat-id">${escapeHtml(katIds.join(' · '))}</span>` : ''}
        </span>
        <span class="v-pill ${pill.cls} klst-cat-status">${escapeHtml(pill.label)}</span>
        ${satz ? `<span class="klst-cat-satz">${escapeHtml(satz)}</span>` : ''}
      </div>`;

  return `
    <div class="klst-detail">
      ${catStrip}
      <!-- Tab bar -->
      <div id="klst-detail-tabs" class="klst-tabs">
        <button type="button" class="klst-tab klst-tab--active" data-detail-tab="overview">
          <span class="v-icon fa-regular fa-circle-info"></span> Übersicht
        </button>
        ${d.is_variant_parent ? `
        <button type="button" class="klst-tab" data-detail-tab="variants">
          <span class="v-icon fa-regular fa-list-check"></span> Varianten
        </button>
        <button type="button" class="klst-tab" data-detail-tab="images">
          <span class="v-icon fa-regular fa-images"></span> Bilder
        </button>` : `
        <button type="button" class="klst-tab" data-detail-tab="mapping">
          <span class="v-icon fa-regular fa-sliders"></span> Mapping
        </button>
        <button type="button" class="klst-tab" data-detail-tab="images">
          <span class="v-icon fa-regular fa-images"></span> Bilder
        </button>
        <button type="button" class="klst-tab" data-detail-tab="preview">
          <span class="v-icon fa-regular fa-eye"></span> Vorschau
        </button>`}
        ${d.is_variant_parent ? '' : `
        <button type="button" class="klst-tabs-publish" id="klst-detail-publish"
                ${pubState.enabled ? '' : 'disabled'} title="${escapeHtml(pubState.title)}">
          ${publishBtnInner(pubState.label)}
        </button>
        <a id="klst-detail-vario-link" href="#" target="_blank" rel="noopener noreferrer"
           class="klst-tabs-vario-link"
           title="Artikel in Vario öffnen, fehlende Werte direkt am Artikel pflegen"
           style="display:none;">
          <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span>
        </a>`}
      </div>

      <!-- Pane: Übersicht -->
      <div data-detail-pane="overview">
        <div class="v-pa-md">
          <div class="v-row v-row-sm klst-detail-actions">
            <!-- 'validating' sperrt auch das Archivieren, nicht nur das Loeschen:
                 Archivieren stoppte den laufenden Poll-Job nicht, der legte nach
                 Kauflands Freigabe trotzdem das Angebot an und setzte 'published'.
                 Das Listing entarchivierte sich damit selbst und bei Kaufland stand
                 ein Angebot, das der Haendler weggelegt hatte. kaufland_archive_listing
                 weist denselben Fall serverseitig ab (Migration 0028). -->
            <div class="v-col-shrink">
              <button type="button" class="v-button" id="klst-detail-archive"
                      ${d.status === 'validating' ? 'disabled title="Kaufland prüft die Stammdaten, bitte das Ergebnis abwarten"' : ''}>
                <span class="v-icon fa-regular fa-box-archive"></span>
                ${escapeHtml(archiveLabel)}
              </button>
            </div>
            <!-- Ausschlussliste ist eine ===-Kette, kein Record<ListingStatus, ...>: der
                 Typecheck kann einen fehlenden Status hier NICHT anmahnen. Bei jedem
                 neuen Status pruefen, ob Loeschen in diesem Zustand sinnvoll ist, und
                 die Liste manuell nachziehen. 'validating' gehoert dazu: das Listing ist
                 bei Kaufland bereits eingereicht, ein Loeschen hier waere lokal spurlos,
                 die Einreichung bei Kaufland liefe weiter und der Worker-Nachtrag von
                 EAN/Preis/Beschreibung liefe ins Leere. kaufland_delete_listing blockt
                 dieselben drei Status serverseitig (siehe Migration 0026). -->
            ${d.status === 'queued' || d.status === 'published' || d.status === 'validating' ? '' : `
              <div class="v-col-shrink">
                <button type="button" class="v-button v-button--ghost klst-detail-delete-btn" id="klst-detail-delete" title="Listing im Status ${escapeHtml(d.status)} aus der Inbox loeschen">
                  <span class="v-icon fa-regular fa-trash"></span>
                  <span>Löschen</span>
                </button>
              </div>`}
          </div>

          ${waitBanner(d)}

          ${produktstandBanner(d)}

          ${d.last_error ? `
            <div class="v-banner v-banner-danger klst-detail-error">
              <span class="v-icon fa-light fa-circle-xmark"></span>
              <div><strong>Letzter Fehler:</strong> ${escapeHtml(d.last_error)}</div>
            </div>` : ''}

          <!-- Hier stand bis zum 2026-08-07 der Abschnitt "Deine Verkaufsbedingungen" mit
               acht Eingabefeldern. Er ist ersatzlos entfallen: Preis, Bestand, Zustand,
               Lieferzeit, Versandgruppe, MwSt-Kennzeichen und Mindestpreis werden alle im
               Reiter "Mapping" zugeordnet, und der Abschnitt liess dieselben Werte ein
               zweites Mal pflegen. Der Listen-Knopf oben loest sie beim Klick aus dem
               Mapping auf (resolveOfferFromMapping), speichert und reiht ein. -->

          <h4 class="klst-detail-section">Daten aus Vario</h4>
          <table class="klst-detail-table">
            <tbody>
              <!-- klst-v-mono zeichnet IDs als Maschinenwerte aus, klst-v-plain den
                   Klartext. Der Entwurf trennt beides ebenso — eine Kategorie in
                   Schreibmaschinenschrift liest sich wie ein technischer Schluessel. -->
              <tr><th>Vario-Artikel-ID</th><td class="klst-v-mono">${escapeHtml(d.vario_article_id)}</td></tr>
              <tr><th>Vario-Listing-ID</th><td class="klst-v-mono">${escapeHtml(d.vario_listing_id)}</td></tr>
              <tr><th>Vario-Kategorie</th><td class="klst-v-plain">${escapeHtml(d.vario_category_name ?? d.vario_category_id ?? '—')}</td></tr>
              <tr><th>EAN</th><td class="klst-v-mono">${escapeHtml(ean ?? '—')}</td></tr>
              <!-- Beschreibung stand hier doppelt: sie wird im Reiter "Mapping" zugeordnet
                   (Kaufland-Pflichtfeld product_data.description, eigene Zeile mit grossem
                   Textfeld), und nur der dort zugeordnete Wert geht wirklich raus. Die Zeile
                   hier zeigte den rohen Snapshot-Wert und legte damit nahe, er sei der
                   gepushte. Entfernt am 2026-08-07. -->
              <tr><th>Variantenvater</th><td class="klst-v-plain">${d.is_variant_parent ? 'Ja' : 'Nein'}</td></tr>
            </tbody>
          </table>

          <h4 class="klst-detail-section">Kaufland</h4>
          <table class="klst-detail-table">
            <tbody>
              <tr><th>Kaufland-Kategorie-ID</th><td class="${d.kaufland_category_id ? 'klst-v-mono' : 'klst-v-plain'}">${d.kaufland_category_id ?? '— (wird beim Push gemappt)'}</td></tr>
              <tr><th>Kaufland-Produkt</th><td class="${d.kaufland_product_id ? 'klst-v-mono' : 'klst-v-plain'}">${escapeHtml(d.kaufland_product_id ?? '—')}</td></tr>
              <tr><th>Kaufland-Unit</th><td class="${d.kaufland_unit_id ? 'klst-v-mono' : 'klst-v-plain'}">${escapeHtml(d.kaufland_unit_id ?? '—')}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pane: Varianten (lazy-rendered beim Tab-Klick) -->
      <div data-detail-pane="variants" hidden></div>

      <!-- Pane: Bilder (lazy-rendered beim Tab-Klick) -->
      <div data-detail-pane="images" hidden></div>

      <!-- Pane: Mapping -->
      <div data-detail-pane="mapping" hidden></div>

      <!-- Pane: Preview (lazy-rendered beim Tab-Klick) -->
      <div data-detail-pane="preview" hidden></div>
    </div>
  `;
}

function stringField(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

// ============================================================================
// Aufschalten-Single-Panel — mode='attach'
// ----------------------------------------------------------------------------
// Wird statt der Mapping/Bilder/Preview-Tabs gerendert, wenn das Listing im
// attach-Modus ist (id_product ist aus EAN-Lookup bekannt). Reduzierter Flow:
//   1. Katalog-Karte (read-only Header) — was wir aus snapshot+DB kennen
//   2. Offer-Form (8 Felder) — vorbefuellt aus kaufland_resolve_offer_defaults
//      + Vario-Live-Werte (listing_price/amount/minimum_price) via field_mappings
//   3. Footer mit Primary "Aufschalten" + Sekundaer "Doch neu listen"
// Submit persistiert in vario_listing_snapshot.offerOverrides (snake-case keys,
// strikt synchron mit Worker T7) und enqueued via kaufland_enqueue_listing_publish.
// ============================================================================

// ---- Vario-Mapping-Sources: callFn-Helper + Resolver --------------------------
// Spiegelt das Pattern aus kaufland-list-mapping.ts. Wir koennten den Helper auch
// hochziehen, aber dann muesste ein gemeinsames ctx-Interface her — dafuer sind
// die paar Zeilen Duplikat nicht teuer genug.

async function callMappingSourcesFn<T>(
  ctx: DetailPaneCtx,
  body: unknown,
): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-mapping-sources`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${ctx.session.token}`,
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

interface PreviewResp {
  unitPayload?: Record<string, unknown>;
  errors?: Array<{ field: string; code: string; message: string }>;
}

/** Ruft `kaufland-listing-preview`. Dieselbe Function, die der Vorschau-Reiter nutzt, damit
 *  gelistet wird, was dort angezeigt wird. */
async function callPreviewFn(
  ctx: DetailPaneCtx,
  body: unknown,
): Promise<{ ok: true; value: PreviewResp } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/kaufland-listing-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const txt = await res.text();
    let parsed: unknown;
    try { parsed = txt ? JSON.parse(txt) : {}; } catch { parsed = { raw: txt }; }
    if (!res.ok) {
      const errBag = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: errBag ? `${errBag.code ?? '?'}: ${errBag.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as PreviewResp };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

interface MappingSourcesResp {
  groups: Array<{
    key: string;
    label: string;
    sources: Array<{
      sourceKey: string;
      path: string;
      dataType: string;
      label: string;
      exampleValue?: unknown;
    }>;
  }>;
  meta?: {
    autoPriceValueCents?: number | null;
    autoStockValue?: number | null;
    autoStockReason?: string | null;
    autoStockStorageLabel?: string | null;
    autoStockStorageIds?: unknown;
    autoVatIndicator?: string | null;
    autoVatRate?: number | null;
    [k: string]: unknown;
  };
}

/** Liest die formatierten `exampleValue`-Strings aus vario-mapping-sources und
 *  versucht eine numerische Interpretation:
 *   - Auto-Sources (auto.listing_price, auto.stock_available) liefern formatierten
 *     String → wir greifen stattdessen direkt auf den Raw-Wert in `meta`.
 *   - Standard-Listing/Article-Sources wie `grossSalesPrice`, `stockAvailable`:
 *     der String kommt aus `String(number)` (siehe truncateForOption) und ist als
 *     EUR/Integer parsebar.
 *  Liefert null wenn weder Match noch parsebar.
 *
 *  Konvertierung: Preise im Vario-Modell sind EUR-Decimals (number) → bleiben EUR;
 *  Auto-Source liefert Cents → wir teilen durch 100. */
async function resolveVarioOfferValues(
  ctx: DetailPaneCtx,
  storefront: string,
  articleId: string,
  listingId: string | null,
  fieldMappings: Record<string, { source?: string; path?: string } | undefined>,
): Promise<ResolvedVarioValues> {
  const empty: ResolvedVarioValues = {
    listingPriceEur: null, amount: null, minimumPriceEur: null,
    vatIndicator: null, vatRate: null, hints: {},
  };
  const res = await callMappingSourcesFn<MappingSourcesResp>(ctx, {
    action: 'list',
    storefront,
    articleId,
    ...(listingId ? { listingId } : {}),
  });
  if (!res.ok) {
    console.warn('[listing-detail-pane] vario-mapping-sources for attach pre-fill failed:', res.error);
    return empty;
  }

  // Index alle Sources nach path UND nach sourceKey (damit wir bei matching gegen
  // den vom User in field_mappings.path eingetragenen Wert flexibel sind).
  const byPath = new Map<string, { sourceKey: string; exampleValue?: unknown }>();
  for (const g of res.value.groups ?? []) {
    for (const s of g.sources ?? []) {
      if (s.path) byPath.set(s.path, { sourceKey: s.sourceKey, exampleValue: s.exampleValue });
    }
  }

  const meta = res.value.meta ?? {};
  const autoPriceCents = typeof meta.autoPriceValueCents === 'number' ? meta.autoPriceValueCents : null;
  const autoStock = typeof meta.autoStockValue === 'number' ? meta.autoStockValue : null;

  // Strings vom Format "12.34" (Article/Listing) ODER "12,34 € (aus Stammdaten)"
  // (Auto-Source) ODER "5 verfügbar" parsen.
  const parseLeadingNumber = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v !== 'string') return null;
    // Erstes Zahlen-Token greifen (de-DE: Komma als Dezimaltrenner moeglich).
    const m = v.match(/-?\d+(?:[.,]\d+)?/);
    if (!m) return null;
    const n = Number(m[0].replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const resolveOne = (
    fieldKey: 'listing_price' | 'amount' | 'minimum_price',
  ): { value: number | null; hint?: string } => {
    const mapping = fieldMappings[fieldKey];
    const path = typeof mapping?.path === 'string' && mapping.path.length > 0 ? mapping.path : null;
    if (path) {
      const src = byPath.get(path);
      if (src) {
        // Auto-Sources: Raw aus meta bevorzugen (exampleValue ist formatiert).
        if (src.sourceKey === 'auto.listing_price' && autoPriceCents !== null) {
          return { value: autoPriceCents / 100, hint: path };
        }
        if (src.sourceKey === 'auto.stock_available' && autoStock !== null) {
          return { value: autoStock, hint: path };
        }
        const parsed = parseLeadingNumber(src.exampleValue);
        if (parsed !== null) return { value: parsed, hint: path };
      }
    }
    // Path nicht konfiguriert ODER nicht aufloesbar → Auto-Fallback (best effort).
    if (fieldKey === 'listing_price' && autoPriceCents !== null) {
      return { value: autoPriceCents / 100, hint: 'auto.listing_price' };
    }
    if (fieldKey === 'amount' && autoStock !== null) {
      return { value: autoStock, hint: 'auto.stock_available' };
    }
    return { value: null };
  };

  const lp = resolveOne('listing_price');
  const am = resolveOne('amount');
  const mp = resolveOne('minimum_price');
  const autoVatIndicator = typeof meta.autoVatIndicator === 'string' && meta.autoVatIndicator.length > 0
    ? meta.autoVatIndicator : null;
  const autoVatRate = typeof meta.autoVatRate === 'number' ? meta.autoVatRate : null;
  return {
    listingPriceEur: lp.value,
    amount: am.value !== null ? Math.floor(am.value) : null,
    minimumPriceEur: mp.value,
    vatIndicator: autoVatIndicator,
    vatRate: autoVatRate,
    hints: {
      ...(lp.hint ? { listing_price: lp.hint } : {}),
      ...(am.hint ? { amount: am.hint } : {}),
      ...(mp.hint ? { minimum_price: mp.hint } : {}),
    },
    // Roh fuer den Klicktext „N verfügbar übernehmen": die Kaskade ueber die in den
    // Kaufland-Einstellungen zugeordneten Lager, unabhaengig davon, worauf `amount` gemappt ist.
    autoStock: {
      value: autoStock,
      reason: typeof meta.autoStockReason === 'string' ? meta.autoStockReason : null,
      storageLabel: typeof meta.autoStockStorageLabel === 'string' && meta.autoStockStorageLabel.length > 0
        ? meta.autoStockStorageLabel : null,
      storageCount: Array.isArray(meta.autoStockStorageIds) ? meta.autoStockStorageIds.length : 0,
    },
  };
}

/** Alles, was der Abschnitt „Deine Verkaufsbedingungen" zum Rendern braucht.
 *  Identisch fuer beide Modi, deshalb EIN Loader statt zweier Kopien. */
interface OfferFormSources {
  defaults: OfferDefaults;
  shippingGroups: ShippingGroupRow[];
  vatIndicators: VatIndicatorRow[];
  vario: ResolvedVarioValues;
}

/** Laedt Offer-Defaults, Versandgruppen, MwSt-Indikatoren und die Vario-Aufloesung.
 *
 *  Die Vario-Aufloesung laeuft NACH den Defaults, weil sie deren `field_mappings`
 *  braucht. Sie ist optional: ein Vario-Hickup fuehrt zu leerer Vorbefuellung, der
 *  Haendler traegt dann von Hand ein. Ein Fehler der Defaults-RPC ist dagegen hart,
 *  ohne sie waeren Versandgruppe und MwSt unbelegt. */
async function loadOfferFormSources(
  ctx: DetailPaneCtx,
  detail: ListingDetail,
): Promise<{ ok: true; sources: OfferFormSources } | { ok: false; error: string }> {
  const [defaultsRes, shippingRes, vatRes] = await Promise.all([
    ctx.supabase.rpc('kaufland_resolve_offer_defaults', { _listing_id: detail.id }),
    ctx.supabase.schema('kaufland').from('shipping_groups')
      .select('id_shipping_group, name')
      .eq('storefront', detail.storefront)
      .order('name'),
    ctx.supabase.schema('kaufland').from('vat_indicators')
      .select('vat_indicator, label, vat_rate')
      .eq('storefront', detail.storefront)
      .order('vat_indicator'),
  ]);

  if (defaultsRes.error) return { ok: false, error: defaultsRes.error.message };

  const defaults = (defaultsRes.data ?? {}) as OfferDefaults;
  const fieldMappings = (defaults.field_mappings ?? {}) as Record<string, { source?: string; path?: string } | undefined>;
  const vario = await resolveVarioOfferValues(
    ctx,
    detail.storefront,
    detail.vario_article_id,
    detail.vario_listing_id ?? null,
    fieldMappings,
  );

  return {
    ok: true,
    sources: {
      defaults,
      shippingGroups: (shippingRes.data ?? []) as ShippingGroupRow[],
      vatIndicators: (vatRes.data ?? []) as VatIndicatorRow[],
      vario,
    },
  };
}

/**
 * Loest die Verkaufsbedingungen ueber die Vorschau-Kette auf und macht daraus die
 * offerOverrides.
 *
 * Warum ueber die Vorschau-Function und nicht ueber resolveVarioOfferValues: Das Mapping IST
 * die Konfiguration. Preis, Bestand, Zustand, Lieferzeit, Versandgruppe, MwSt-Kennzeichen und
 * Mindestpreis stehen alle als `field_group='offer'` in `kaufland.listing_field_mappings`,
 * und `kaufland-listing-preview` loest genau die auf und legt sie in `unitPayload` ab.
 * `resolveVarioOfferValues` liest dagegen `offer_default_profiles.field_mappings`, eine
 * andere Quelle, die live in jeder Profilzeile leer ist; sie deckt ausserdem nur Preis,
 * Bestand und Mindestpreis ab. Damit kaeme fuer vier der sieben Felder etwas anderes heraus
 * als in der Vorschau.
 *
 * Nebenwirkung, die man kennen muss: Gepusht wird, was der Haendler in DIESEM Moment
 * angezeigt bekommen wuerde. Der Bestand ist also der vom KLICK-Zeitpunkt, nicht der vom
 * Push-Zeitpunkt; zwischen Einreichung und Kauflands Freigabe koennen Stunden liegen. Die
 * Alternative waere, die `auto.*`-Kaskaden im Worker noch einmal zu bauen, und genau dagegen
 * hat die Produktanlage entschieden (siehe `loadVarioFacts` in
 * `apps/worker/src/lib/kaufland-product-data-input.ts`). Der laufende Bestandsabgleich
 * korrigiert den Wert nach dem Listen ohnehin.
 */
async function resolveOfferFromMapping(
  ctx: DetailPaneCtx,
  detail: ListingDetail,
): Promise<{ ok: true; overrides: Record<string, unknown> } | { ok: false; message: string }> {
  if (detail.kaufland_category_id === null || detail.kaufland_category_id === undefined) {
    return { ok: false, message: 'Dem Listing ist noch keine Kaufland-Kategorie zugeordnet. Bitte im Reiter „Mapping" eine Kategorie waehlen.' };
  }

  // 1. Mapping-Profil holen. Identischer Aufruf wie im Vorschau-Reiter, damit hier nicht ein
  //    anderes Profil erwischt wird als das, was der Haendler dort sieht.
  const locale = localeForStorefront(detail.storefront);
  const { data: profileResp, error: profileErr } = await ctx.supabase.rpc('kaufland_get_mapping_profile', {
    _storefront: detail.storefront,
    _locale: locale,
    _category_id: detail.kaufland_category_id,
  });
  if (profileErr) return { ok: false, message: `Mapping-Profil konnte nicht geladen werden: ${profileErr.message}` };
  const profile = (profileResp as { profile?: { id: string } | null } | null)?.profile ?? null;
  if (!profile) {
    return { ok: false, message: 'Fuer diese Kategorie ist noch kein Mapping gespeichert. Bitte im Reiter „Mapping" die Felder zuordnen und speichern.' };
  }

  // 2. Offer-Defaults parallel zur Vorschau: Versandgruppe und MwSt duerfen von dort fallen.
  const [previewRes, defaultsRes] = await Promise.all([
    callPreviewFn(ctx, {
      profileId: profile.id,
      articleId: detail.vario_article_id,
      listingId: detail.vario_listing_id,
    }),
    ctx.supabase.rpc('kaufland_resolve_offer_defaults', { _listing_id: detail.id }),
  ]);

  if (!previewRes.ok) return { ok: false, message: `Verkaufsbedingungen konnten nicht aufgeloest werden: ${previewRes.error}` };
  if (defaultsRes.error) return { ok: false, message: `Vorbelegung konnte nicht geladen werden: ${defaultsRes.error.message}` };

  const unit = (previewRes.value.unitPayload ?? {}) as Record<string, unknown>;
  const defaults = (defaultsRes.data ?? null) as { id_shipping_group?: string | null; vat_indicator?: string | null } | null;

  const aufgeloest = resolveOfferOverridesFromUnit(unit, defaults);
  if (aufgeloest.ok) return { ok: true, overrides: aufgeloest.overrides };

  // Die Vorschau kennt zu den Auto-Quellen bereits fertige deutsche Begruendungen
  // (AUTO_PRICE_UNRESOLVED, AUTO_STOCK_UNRESOLVED, AUTO_VAT_UNRESOLVED). Sie sind deutlich
  // konkreter als eine allgemeine "fehlt"-Meldung, deshalb haben sie Vorrang.
  const betroffen = new Set<string>(aufgeloest.missing);
  const diagnosen = (previewRes.value.errors ?? [])
    .filter((e) => betroffen.has(e.field))
    .map((e) => e.message);
  return { ok: false, message: missingOfferFieldsMessage(aufgeloest.missing, diagnosen) };
}

/**
 * Rendert die Modus-Entscheidung fuer ein 'undecided'-Listing (Migration 0058).
 *
 * Ersetzt weder das Aufschalten- noch das Neu-Listen-Panel, sondern steht davor:
 * Der Haendler waehlt hier per `kaufland_set_listing_mode`, danach uebernimmt
 * `onReload()` den Wechsel in den passenden Zweig (renderListingDetailPane liest
 * `mode` bei jedem Aufruf neu).
 *
 * Aufschalten wird nur angeboten, wenn `bestimmeModusWahl()` einen Katalogtreffer
 * sieht (attach_id_product gesetzt) -- sonst steht der Grund als Text da, statt
 * einer ausgegrauten Schaltflaeche ohne Erklaerung. Neu-Listen ist IMMER
 * angeboten, verlangt aber die zweistufige Bestaetigung von wireTwoStepConfirm:
 * Kaufland hat kein Loeschen fuer Katalogprodukte, ein versehentlicher Klick waere
 * nicht rueckgaengig zu machen (anders als der Wechsel zurueck von 'attach' auf
 * 'new' im bestehenden Aufschalten-Panel, wo noch nichts eingereicht ist).
 */
async function renderModeChoicePanel(
  host: HTMLElement,
  ctx: DetailPaneCtx,
  detail: ListingDetail,
  onReload: () => void,
): Promise<void> {
  const wahl = bestimmeModusWahl(detail.attach_id_product);
  const sf = (STOREFRONT_LABELS[detail.storefront] ?? detail.storefront).toUpperCase();

  host.innerHTML = `
    <div class="klst-attach v-pa-md">
      <div class="klst-attach-header">
        <div class="klst-attach-header-title">
          <span class="v-icon fa-regular fa-circle-question"></span>
          Modus waehlen
        </div>
        <div class="klst-attach-header-pills">
          <span class="v-pill v-pill--muted">Unentschieden</span>
          <span class="v-pill v-pill--muted">${escapeHtml(sf)}</span>
        </div>
      </div>

      <div class="v-banner v-banner-info klst-attach-info">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Fuer dieses Angebot ist noch nicht entschieden, ob es an ein bestehendes
          Kaufland-Produkt angehaengt oder als eigenes Produkt neu gelistet wird. Bitte waehlen.</div>
      </div>

      <h4 class="klst-detail-section">Aufschalten</h4>
      <div class="v-banner v-banner-info klst-attach-info">
        <span class="v-icon fa-light fa-link"></span>
        <div>
          Titel, Bilder und Attribute kommen vom bestehenden Kaufland-Katalogeintrag. Eigene
          Angaben zu diesen Feldern bleiben wirkungslos -- nur Deine Verkaufsbedingungen
          (Preis, Bestand, Versand) zaehlen.
          ${wahl.attachMoeglich
            ? `<div class="klst-attach-hint">Katalogtreffer: Kaufland-Produkt-ID <code>${escapeHtml(String(detail.attach_id_product))}</code></div>`
            : ''}
        </div>
      </div>
      ${wahl.attachMoeglich
        ? `<button type="button" class="v-button v-button-primary" id="klst-mode-attach">
             <span class="v-icon fa-regular fa-link"></span> Aufschalten waehlen
           </button>`
        : `<div class="v-banner v-banner-warning klst-attach-info">
             <span class="v-icon fa-light fa-triangle-exclamation"></span>
             <div>${escapeHtml(wahl.grundWennNicht ?? '')}</div>
           </div>`
      }

      <h4 class="klst-detail-section">Neu listen</h4>
      <div class="v-banner v-banner-info klst-attach-info">
        <span class="v-icon fa-light fa-plus"></span>
        <div>
          Legt ein eigenes Produkt im Kaufland-Katalog an: Titel, Bilder, Beschreibung und
          Attribute kommen von Dir. Kaufland hat kein Loeschen fuer Katalogprodukte -- diese
          Entscheidung laesst sich danach nicht mehr rueckgaengig machen.
        </div>
      </div>
      <button type="button" class="v-button" id="klst-mode-new">
        <span class="v-icon fa-regular fa-plus"></span> Neu listen
      </button>

      <div class="klst-attach-footer">
        <div class="klst-attach-status" id="klst-mode-status"></div>
      </div>
    </div>
  `;

  const statusEl = host.querySelector<HTMLElement>('#klst-mode-status');
  function setStatus(msg: string, kind: 'info' | 'error'): void {
    if (!statusEl) return;
    statusEl.textContent = msg;
    statusEl.className = `klst-attach-status klst-attach-status--${kind}`;
  }

  async function doSetMode(mode: 'attach' | 'new'): Promise<void> {
    const { error: modeErr } = await ctx.supabase.rpc('kaufland_set_listing_mode', {
      _listing_id: detail.id,
      _mode: mode,
    });
    if (modeErr) {
      setStatus(`Modus setzen fehlgeschlagen: ${modeErr.message}`, 'error');
      return;
    }
    showToast(
      mode === 'attach' ? 'Modus auf "Aufschalten" gesetzt.' : 'Modus auf "Neu listen" gesetzt.',
      'success',
    );
    onReload();
  }

  const attachBtn = host.querySelector<HTMLButtonElement>('#klst-mode-attach');
  attachBtn?.addEventListener('click', () => void doSetMode('attach'));

  // Zweistufige Bestaetigung (Projektregel: kein window.confirm) -- erster Klick
  // schaerft den Button, zweiter loest aus. Siehe Kopfkommentar dieser Funktion.
  const newBtn = host.querySelector<HTMLButtonElement>('#klst-mode-new');
  if (newBtn) {
    wireTwoStepConfirm(
      newBtn,
      `<span class="v-icon fa-regular fa-triangle-exclamation"></span> Wirklich neu listen? Legt ein nicht löschbares Katalogprodukt an`,
      () => void doSetMode('new'),
    );
  }
}

/**
 * Vorschlag fuer „xx,xx € übernehmen" neben dem Verkaufspreis: der aktuelle Stammdaten-VK
 * des Vario-Artikels, fuer CZ/PL mit Varios Tageskurs in die Storefront-Waehrung
 * umgerechnet (Edge Function kaufland-price-suggestion). Fail-soft wie die Vorbefuellung
 * aus vario-mapping-sources: ohne Antwort kein Knopf, der Grund steht in der Konsole.
 */
async function loadPriceSuggestion(ctx: DetailPaneCtx, detail: ListingDetail): Promise<PreisvorschlagAnzeige> {
  const res = await callFn<PriceSuggestionResp>(ctx, 'kaufland-price-suggestion', {
    articleId: detail.vario_article_id,
    storefront: detail.storefront,
  });
  if (!res.ok) {
    console.warn('[listing-detail-pane] kaufland-price-suggestion failed:', res.error);
    return { suggestion: null };
  }
  return beschreibePreisvorschlag(res.value);
}

async function renderAttachPanel(
  host: HTMLElement,
  ctx: DetailPaneCtx,
  detail: ListingDetail,
  onReload: () => void,
): Promise<void> {
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Aufschalt-Daten…</div>`;

  // Kategorie-Label ist attach-spezifisch (Katalog-Karte) und laeuft deshalb neben
  // dem geteilten Loader, nicht in ihm.
  const categoryPromise: Promise<{ data: { name: string | null } | null; error: { message: string } | null }> =
    detail.kaufland_category_id != null
      ? (ctx.supabase.schema('kaufland').from('categories')
          .select('name')
          .eq('storefront', detail.storefront)
          .eq('category_id', detail.kaufland_category_id)
          .maybeSingle() as unknown as Promise<{ data: { name: string | null } | null; error: { message: string } | null }>)
      : Promise.resolve({ data: null, error: null });

  const [sourcesRes, categoryRes, preisVorschlag] = await Promise.all([
    loadOfferFormSources(ctx, detail),
    categoryPromise,
    // Nur wenn das Formular ueberhaupt bearbeitbar ist: bei published/archived gibt es
    // kein Preisfeld, also auch keinen Grund fuer den Vario-Roundtrip.
    isOfferFormEditable(detail.status)
      ? loadPriceSuggestion(ctx, detail)
      : Promise.resolve<PreisvorschlagAnzeige>({ suggestion: null }),
  ]);

  if (!sourcesRes.ok) {
    host.innerHTML = `<div class="klst-loading klst-loading--error">Defaults laden fehlgeschlagen: ${escapeHtml(sourcesRes.error)}</div>`;
    return;
  }
  const { defaults, shippingGroups, vatIndicators, vario } = sourcesRes.sources;
  const categoryLabel = categoryRes.data?.name ?? null;

  // Bestehende Overrides aus dem Snapshot vorbefuellen, falls der User schon
  // mal gespeichert hatte (nicht-published Status ist editierbar).
  const snapshot = (detail.vario_listing_snapshot ?? {}) as Record<string, unknown>;
  const existingOverrides = (snapshot['offerOverrides'] ?? {}) as Record<string, unknown>;
  const ean = stringField(snapshot, 'ean');

  const prefill = buildOfferFormPrefill({ existingOverrides, defaults, vario, vatIndicators });
  const bestandsVorschlag = beschreibeBestandsvorschlag(
    vario.autoStock ?? { value: null, reason: null, storageLabel: null, storageCount: 0 },
  );

  const sf = (STOREFRONT_LABELS[detail.storefront] ?? detail.storefront).toUpperCase();
  const statusPill = STATUS_PILL[detail.status] ?? STATUS_PILL.pending;
  // Dieselbe Regel wie im Ueberblick-Abschnitt, eine Stelle statt zweier. Begruendung
  // der Zustandsliste steht am Helfer in offer-conditions-form.ts.
  const isReadonly = !isOfferFormEditable(detail.status);
  // Und dieselbe zweite Frage: bearbeitbar ist nicht gleich aufschaltbar. Trifft
  // hier 'archived' - das Formular waere bearbeitbar, kaufland_save_attach_overrides
  // laesst den Status aber nicht zu. Im Attach-Panel gibt es heute keinen
  // Archivieren-Knopf, der Zustand kommt also nur von aussen (Angebotsliste, MCP,
  // direkter RPC-Aufruf). Die Regel gehoert trotzdem an beide Formulare, sonst
  // haengt sie an der Frage, welcher Knopf zufaellig danebensteht.
  const gate = offerSubmitGate(detail.status, 'attach');

  // ---- Render ---------------------------------------------------------------
  host.innerHTML = `
    <div class="klst-attach v-pa-md">
      <div class="klst-attach-header">
        <div class="klst-attach-header-title">
          <span class="v-icon fa-regular fa-link"></span>
          Aufschalten auf Kaufland-Katalog-Eintrag
        </div>
        <div class="klst-attach-header-pills">
          <span class="v-pill v-pill--info">Aufschalten</span>
          <span class="v-pill ${statusPill.cls}">${escapeHtml(statusPill.label)}</span>
          <span class="v-pill v-pill--muted">${escapeHtml(sf)}</span>
          <a id="klst-attach-vario-link" href="#" target="_blank" rel="noopener noreferrer"
             class="klst-tabs-vario-link"
             title="Artikel in Vario öffnen, fehlende Werte direkt am Artikel pflegen"
             style="display:none;">
            <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span>
          </a>
        </div>
      </div>

      <div class="v-banner v-banner-info klst-attach-info">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Dieses Angebot existiert bereits bei Kaufland. Beim Aufschalten werden nur Deine Verkaufsbedingungen ergaenzt. Produkt-Stammdaten, Bilder und Beschreibung kommen aus dem Katalog-Eintrag.</div>
      </div>

      <h4 class="klst-detail-section">Katalog-Eintrag</h4>
      <table class="klst-detail-table">
        <tbody>
          <tr><th>Artikel (Vario)</th><td>${escapeHtml(detail.vario_article_name ?? detail.vario_article_id)}</td></tr>
          <tr><th>SKU (id_offer)</th><td>${
            detail.vario_article_number
              ? `<a id="klst-attach-sku-link" href="#" target="_blank" rel="noopener noreferrer"
                    class="klst-attach-sku-link" style="display:none;"
                    title="Artikel in Vario öffnen"><code>${escapeHtml(detail.vario_article_number)}</code>
                    <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span></a>
                  <code id="klst-attach-sku-text">${escapeHtml(detail.vario_article_number)}</code>`
              : '<span class="v-text-muted">— wird beim ersten Push aus Vario geladen</span>'
          }</td></tr>
          <tr><th>EAN</th><td>${escapeHtml(ean ?? '—')}</td></tr>
          <tr><th>Kaufland-Produkt-ID</th><td>${detail.attach_id_product != null ? `<code>${escapeHtml(String(detail.attach_id_product))}</code>` : '—'}</td></tr>
          <tr><th>Kaufland-Kategorie</th><td>${escapeHtml(categoryLabel ?? (detail.kaufland_category_id != null ? `ID ${detail.kaufland_category_id}` : '—'))}</td></tr>
        </tbody>
      </table>

      ${waitBanner(detail)}

      ${detail.last_error ? `
        <div class="v-banner v-banner-danger klst-detail-error">
          <span class="v-icon fa-light fa-circle-xmark"></span>
          <div><strong>Letzter Fehler:</strong> ${escapeHtml(detail.last_error)}</div>
        </div>` : ''}

      ${isReadonly ? `
        <div class="v-banner v-banner-success klst-attach-info" style="margin-top:1em;">
          <span class="v-icon fa-light fa-circle-check"></span>
          <div>${unitSatz(detail)} Verkaufspreis, Lagerbestand und weitere Bedingungen verwaltest Du jetzt im Tab <strong>Angebote</strong>.</div>
        </div>
      ` : `
      <h4 class="klst-detail-section">Deine Verkaufsbedingungen</h4>
      <form id="klst-attach-form" data-offer-form class="klst-attach-form" autocomplete="off">
        ${renderOfferFormFields(prefill, {
          editable: !isReadonly,
          shippingGroups,
          vatIndicators,
          storefrontLabel: sf,
          // „xx,xx € übernehmen" neben dem Titel: Stammdaten-VK aus Vario, fuer CZ/PL
          // umgerechnet. Nur ein Klick traegt ihn ein, nichts wird still vorbelegt.
          priceTakeoverHtml: renderPriceTakeover(preisVorschlag.suggestion, preisVorschlag),
          // „N verfügbar übernehmen": Summe ueber die in den Kaufland-Einstellungen
          // zugeordneten Lager (Bestandskaskade aus vario-mapping-sources).
          stockTakeoverHtml: renderStockTakeover(bestandsVorschlag.value, bestandsVorschlag),
        })}

        <div class="klst-attach-footer">
          <button type="submit" class="v-button v-button-primary" id="klst-attach-submit"
                  ${gate.allowed ? '' : `disabled title="${escapeHtml(gate.reason)}"`}>
            <span class="v-icon fa-regular fa-rocket"></span>
            Auf Kaufland aufschalten
          </button>
          <button type="button" class="v-button" id="klst-attach-switch-new" ${detail.status !== 'pending' ? 'disabled title="Wechsel nur im Status \'pending\' moeglich"' : ''}>
            <span class="v-icon fa-regular fa-arrow-rotate-left"></span>
            Doch neu listen
          </button>
          <div class="klst-attach-status" id="klst-attach-status"></div>
        </div>
      </form>
      `}
    </div>
  `;

  // Vario-Artikel-Link in der Pill-Bar (rechts) UND auf der SKU in der Katalog-
  // Tabelle — beide async populiert wenn die tenant-base-URL geladen ist.
  // Identisches Pattern wie im Tabs-Header (mode='new').
  const varioLink = host.querySelector<HTMLAnchorElement>('#klst-attach-vario-link');
  const skuLink = host.querySelector<HTMLAnchorElement>('#klst-attach-sku-link');
  const skuText = host.querySelector<HTMLElement>('#klst-attach-sku-text');
  if (varioLink || skuLink) {
    void getVarioBaseUrl(ctx.supabase).then((baseUrl) => {
      if (!baseUrl) return;
      const url = `${baseUrl}/products/articles/${encodeURIComponent(detail.vario_article_id)}/detail`;
      if (varioLink) {
        varioLink.href = url;
        varioLink.style.display = '';
      }
      // SKU: bevor Vario-URL da ist, zeigen wir den nackten <code>-Text; sobald
      // verfuegbar wechseln wir auf den klickbaren Link.
      if (skuLink) {
        skuLink.href = url;
        skuLink.style.display = '';
        if (skuText) skuText.style.display = 'none';
      }
    });
  }

  // Auch hier, sonst haette der Aufschalt-Bereich ein Wartebanner ohne Knopf.
  wireStatusRefresh(host, ctx, detail.id);

  // ---- Submit-Handler -------------------------------------------------------
  const form = host.querySelector<HTMLFormElement>('#klst-attach-form');
  const statusEl = host.querySelector<HTMLElement>('#klst-attach-status');
  const submitBtn = host.querySelector<HTMLButtonElement>('#klst-attach-submit');
  const switchBtn = host.querySelector<HTMLButtonElement>('#klst-attach-switch-new');

  form?.addEventListener('submit', (ev) => {
    ev.preventDefault();
    void doAttachSubmit();
  });
  // Klick auf „xx,xx € übernehmen" bzw. „N verfügbar übernehmen" traegt den Vorschlag ins
  // jeweilige Feld ein (field-takeover.ts).
  if (form) wireTakeover(form);
  if (switchBtn) {
    wireTwoStepConfirm(
      switchBtn,
      `<span class="v-icon fa-regular fa-arrow-rotate-left"></span> Aufschalt-Konfiguration verwerfen?`,
      () => void doSwitchToNew(),
    );
  }

  async function doAttachSubmit(): Promise<void> {
    if (!form || !submitBtn) return;

    submitBtn.disabled = true;
    // Lesen, pruefen, speichern, einreihen laeuft im geteilten submitOfferForm().
    // Derselbe Weg wie im Ueberblick-Abschnitt und in der Kopfleiste.
    const ok = await submitOfferForm(ctx, detail.id, form, setStatus);
    if (!ok) {
      submitBtn.disabled = false;
      return;
    }

    showToast('Listing zur Aufschaltung eingestellt.', 'success');
    setStatus('Push laeuft, warte auf Kaufland …', 'info');
    // Statt onReload() (das die Inbox neu laedt und das Detail via Loading-
    // Spinner ueberschreibt): in-place Status-Polling. Pollt alle 2s den
    // Listing-Status + last_error und aktualisiert NUR die Pill-Bar und das
    // Error-Banner im DOM. Stoppt bei published/error oder nach ~40s.
    pollStatusUntilTerminal();
  }

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  function stopPolling(): void {
    if (pollTimer !== null) { clearTimeout(pollTimer); pollTimer = null; }
  }
  function pollStatusUntilTerminal(): void {
    stopPolling();
    let attempts = 0;
    const MAX = 20; // 20 * 2s = 40s
    const tick = async (): Promise<void> => {
      // Host wurde ersetzt/entfernt (Re-Mount im Slot oder Lister-Reload) —
      // Polling beenden statt ins tote DOM zu schreiben (Review-Befund Task 4).
      if (!host.isConnected) { stopPolling(); return; }
      // innerHTML-Re-Mount: Container existiert noch, aber unser Form-DOM ist weg.
      if (form && !form.isConnected) { stopPolling(); return; }
      attempts++;
      const { data, error: pollErr } = await ctx.supabase.rpc('kaufland_get_listing_detail', { _id: detail.id });
      if (pollErr) {
        console.warn('[listing-detail-pane attach-poll] failed:', pollErr.message);
        if (attempts < MAX) pollTimer = setTimeout(() => void tick(), 2000);
        return;
      }
      const fresh = (Array.isArray(data) ? data[0] : data) as ListingDetail | null | undefined;
      if (!fresh) return;
      applyLiveStatus(host, fresh);
      if (fresh.status === 'published' || fresh.status === 'error') {
        // Terminal: stoppen + Inbox einmalig refreshen damit dortige Pill
        // (queued -> published/error) konsistent ist.
        pollTimer = null;
        onReload();
        return;
      }
      if (attempts < MAX) pollTimer = setTimeout(() => void tick(), 2000);
    };
    void tick();
  }

  async function doSwitchToNew(): Promise<void> {
    stopPolling();
    const { error: switchErr } = await ctx.supabase.rpc('kaufland_switch_listing_to_new', { _listing_id: detail.id });
    if (switchErr) {
      setStatus(`Wechsel fehlgeschlagen: ${switchErr.message}`, 'error');
      return;
    }
    showToast('Modus auf "Neu listen" gewechselt.', 'success');
    onReload();
  }

  function setStatus(msg: string, kind: 'info' | 'error'): void {
    if (!statusEl) return;
    statusEl.textContent = msg;
    statusEl.className = `klst-attach-status klst-attach-status--${kind}`;
  }
}

/** Aktualisiert die Status-Pill und das last_error-Banner im offenen Aufschalt-
 *  Panel, ohne das ganze DOM neu zu rendern. Wird vom Polling-Loop nach Submit
 *  benutzt, damit der User Status-Uebergaenge (pending → queued → published/
 *  error) live sieht ohne dass das Form verschwindet. */
function applyLiveStatus(host: HTMLElement, fresh: ListingDetail): void {
  // 1. Status-Pill in der Header-Pill-Bar austauschen (zweites .v-pill nach
  //    der "Aufschalten"-Pill).
  const pillBar = host.querySelector<HTMLElement>('.klst-attach-header-pills');
  if (pillBar) {
    const pills = pillBar.querySelectorAll<HTMLElement>('.v-pill');
    const statusPillEl = pills[1]; // 0=Aufschalten, 1=Status, 2=Storefront
    if (statusPillEl) {
      const pill = STATUS_PILL[fresh.status] ?? STATUS_PILL.pending;
      statusPillEl.className = `v-pill ${pill.cls}`;
      statusPillEl.textContent = pill.label;
    }
  }

  // 2. last_error-Banner: existing entfernen wenn keine Message, sonst einfuegen/
  //    aktualisieren ueber der Form (unter der Katalog-Tabelle).
  const existingErrBanner = host.querySelector<HTMLElement>('.klst-detail-error');
  if (fresh.last_error) {
    const escapedErr = fresh.last_error
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    if (existingErrBanner) {
      const msgEl = existingErrBanner.querySelector('div');
      if (msgEl) msgEl.innerHTML = `<strong>Letzter Fehler:</strong> ${escapedErr}`;
    } else {
      const formEl = host.querySelector<HTMLElement>('.klst-attach-form');
      if (formEl) {
        const banner = document.createElement('div');
        banner.className = 'v-banner v-banner-danger klst-detail-error';
        banner.innerHTML = `<span class="v-icon fa-light fa-circle-xmark"></span>
          <div><strong>Letzter Fehler:</strong> ${escapedErr}</div>`;
        formEl.parentNode?.insertBefore(banner, formEl);
      }
    }
  } else if (existingErrBanner) {
    existingErrBanner.remove();
  }

  // 3. Status-Footer-Text und Submit-Button entsprechend setzen.
  const statusEl = host.querySelector<HTMLElement>('#klst-attach-status');
  const submitBtn = host.querySelector<HTMLButtonElement>('#klst-attach-submit');
  if (fresh.status === 'published') {
    if (statusEl) {
      statusEl.textContent = 'Erfolgreich auf Kaufland aufgeschaltet.';
      statusEl.className = 'klst-attach-status klst-attach-status--info';
    }
    if (submitBtn) submitBtn.disabled = true;
  } else if (fresh.status === 'error') {
    if (statusEl) {
      statusEl.textContent = 'Fehler beim Push, siehe Banner. Felder ggf. anpassen und erneut "Auf Kaufland aufschalten" druecken.';
      statusEl.className = 'klst-attach-status klst-attach-status--error';
    }
    if (submitBtn) submitBtn.disabled = false;
  }
}

